/**
 * Customer portal guided tour — step definitions and localStorage helpers.
 * Bump TOUR_VERSION / PAGE_TOUR_VERSION when steps change meaningfully so users
 * see the relevant tour again.
 */
export const TOUR_VERSION = "v1";
export const PAGE_TOUR_VERSION = "v1";

export const PORTAL_TOUR_STEPS = [
  {
    target: "body",
    placement: "center",
    title: "Welcome to the AIxMed portal",
    content:
      "This short tour highlights the main areas of the customer portal. You can skip anytime and restart later from Account.",
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-dashboard"]',
    title: "Dashboard",
    content:
      "View jobs submitted and processed over time for your organization. Filter by application, range, and timezone.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="nav-slides"]',
    title: "Slides",
    content:
      "Look up slides, upload images from the browser, and review upload activity. Open the menu for Lookup, Upload, and Upload Activity.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="nav-reports"]',
    title: "Reports",
    content:
      "Download scheduled and available reports for your organization.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="nav-organization"]',
    title: "Organization",
    content:
      "Manage org information and contacts, users, and email notification subscriptions. Open the menu for Information, Users, and Notifications.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="nav-notices"]',
    title: "Portal notices",
    content:
      "System and maintenance notices appear here. A badge shows when there are active notices to review.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="nav-account"]',
    title: "Account",
    content:
      "Your signed-in identity and password settings. You can also restart this tour from Account anytime.",
    disableBeacon: true,
    placement: "bottom",
  },
];

function storageKey(userKey) {
  const id = String(userKey || "anonymous").trim().toLowerCase() || "anonymous";
  return `aixmed.portal.tour.${TOUR_VERSION}:${id}`;
}

export function hasCompletedPortalTour(userKey) {
  try {
    return localStorage.getItem(storageKey(userKey)) === "1";
  } catch {
    return false;
  }
}

export function markPortalTourCompleted(userKey) {
  try {
    localStorage.setItem(storageKey(userKey), "1");
  } catch {
    // ignore quota / private mode
  }
}

function pageStorageKey(userKey, tourId) {
  const id = String(userKey || "anonymous").trim().toLowerCase() || "anonymous";
  const tour = String(tourId || "page").trim().toLowerCase() || "page";
  return `aixmed.portal.page-tour.${PAGE_TOUR_VERSION}:${tour}:${id}`;
}

export function hasCompletedPageTour(userKey, tourId) {
  try {
    return localStorage.getItem(pageStorageKey(userKey, tourId)) === "1";
  } catch {
    return false;
  }
}

export function markPageTourCompleted(userKey, tourId) {
  try {
    localStorage.setItem(pageStorageKey(userKey, tourId), "1");
  } catch {
    // ignore quota / private mode
  }
}

/** Detailed tours for Slides menu pages. */
export const SLIDES_LOOKUP_TOUR_STEPS = [
  {
    target: '[data-tour="slides-lookup-intro"]',
    title: "Slide Lookup",
    content:
      "Search slides already submitted for your organization. Filters stay in the URL so you can bookmark or share a search.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-lookup-timezone"]',
    title: "Timezone",
    content:
      "All date/time filters and the Started/Finished columns use this timezone. Pick the zone your lab works in.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-lookup-slide-id"]',
    title: "Slide identifier",
    content:
      "Optional. Enter a full or partial slide ID to narrow results. Required when using “Search all time”.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-lookup-applications"]',
    title: "Application filter",
    content:
      "Limit results to one or more catalog applications (for example AIxURO). Leave empty to include all applications enabled for your org.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-lookup-all-time"]',
    title: "Search all time",
    content:
      "When checked, the date range is ignored and every matching slide ID is searched historically. A slide identifier is required for this mode.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-lookup-range"]',
    title: "Date and time range",
    content:
      "When not searching all time, set the start and end of the window. Times are interpreted in the selected timezone.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-lookup-submit"]',
    title: "Look Up",
    content:
      "Run the search with the current filters. Results appear in the table below; use Load more if there are additional pages.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-lookup-results"]',
    title: "Results",
    content:
      "Each row is a processing job: slide ID, application, start/finish times, and status. Click a slide ID (or the row) to open slide details.",
    disableBeacon: true,
    placement: "top",
  },
];

export const SLIDES_UPLOAD_TOUR_STEPS = [
  {
    target: '[data-tour="slides-upload-intro"]',
    title: "Slide Upload",
    content:
      "Upload whole-slide images from the browser. Files go through the same processing pipeline as Smart Agent submissions.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-upload-hint"]',
    title: "Accepted formats",
    content:
      "Only supported slide formats are accepted. You can browse one file at a time or choose a folder to queue every matching file (including subfolders). MRXS packages are not supported here.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-upload-app"]',
    title: "Application",
    content:
      "If your organization has more than one application enabled, pick the default (or folder) application here. You can also change the application per file in the table.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-upload-table"]',
    title: "Upload queue",
    content:
      "Each row is a file to upload. Use Browse file on a row to pick a single slide. Progress appears in the Progress column while an upload is running.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="slides-upload-add-row"]',
    title: "Add another file",
    content:
      "Click + to add an empty row, then Browse file to select another slide without using a folder.",
    disableBeacon: true,
    placement: "left",
  },
  {
    target: '[data-tour="slides-upload-folder"]',
    title: "Choose folder",
    content:
      "Select a local folder to queue all supported slide files found in that folder and its subfolders. Duplicate basenames are blocked so uploads cannot overwrite each other.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="slides-upload-submit"]',
    title: "Upload",
    content:
      "Start transferring queued files. Large files may use multipart upload. You can Cancel an in-progress run; cancelled attempts are recorded in Upload Activity.",
    disableBeacon: true,
    placement: "top",
  },
];

export const SLIDES_UPLOAD_ACTIVITY_TOUR_STEPS = [
  {
    target: '[data-tour="slides-activity-intro"]',
    title: "Upload Activity",
    content:
      "Track each upload session from first detection through cloud processing. Sessions are retained for 30 days.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-activity-refresh"]',
    title: "Auto-refresh and Refresh",
    content:
      "Optionally reload on an interval, or click Refresh to reload now with the current filters.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-activity-timezone"]',
    title: "Timezone",
    content:
      "The search window and all displayed timestamps use this timezone. Underlying records are stored in UTC.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-activity-filters"]',
    title: "Filters",
    content:
      "Narrow by time window, status, channel (agent or portal), filename, hash prefix, uploader, or error text. Changing filters does not reload until you Search.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="slides-activity-search"]',
    title: "Search and Reset",
    content:
      "Search applies the filters. Reset restores the default last-7-days window and clears text filters, then searches again.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="slides-activity-results"]',
    title: "Activity table",
    content:
      "Each row is one upload attempt. Click a row to expand the timeline. For succeeded uploads, open slide details from the Name or Slide column.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="slides-activity-status"]',
    title: "Status meanings",
    content:
      "Statuses move through Detected → Started → Uploading → Uploaded → Pipeline started → Processing → Succeeded / Failed / Cancelled / Skipped. Hover the Status column tip anytime for the full glossary.",
    disableBeacon: true,
    placement: "top",
  },
];
